import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useStyles } from '@/contexts/StyleProvider';

const PublicHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { styles } = useStyles() || { styles: null };

  const navLinks = [
    { to: '/services', label: 'Services' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/blog', label: 'Insights' },
    { to: '/contact', label: 'Contact' },
    { to: '/about', label: 'About' },
  ];

  const isActive = (path) => location.pathname === path;

  // Use CSS variables or fallback
  const headerStyle = {
    backgroundColor: 'var(--color-header-bg, #FFFFFF)',
    height: `calc(var(--layout-header-height, 7) * 1rem)`
  };

  const logoStyle = {
    height: `calc(var(--layout-logo-height, 5) * 1rem)`
  };

  const navLinkStyle = (active) => ({
    color: active ? 'var(--color-primary, #1F3C4F)' : 'var(--color-header-text, #475569)',
    fontWeight: active ? '600' : '500'
  });

  const buttonStyle = {
    backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
    color: 'var(--color-button-text, #FFFFFF)',
    borderRadius: 'var(--btn-computed-radius, 0.5rem)'
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b border-slate-100"
      style={headerStyle}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src="/spt-logo.png" 
              alt="SPT Digital Marketing" 
              style={logoStyle}
              className="w-auto"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className="font-medium transition-all duration-200 hover:text-[var(--color-primary,#1F3C4F)] relative group"
                style={navLinkStyle(isActive(link.to))}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-[var(--color-primary,#1F3C4F)] transition-all duration-200 ${isActive(link.to) ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </Link>
            ))}
          </div>
          
          <div className="hidden md:flex items-center gap-4">
            <Link to="/login">
              <Button 
                variant="ghost" 
                style={{ color: 'var(--color-header-text, #475569)' }}
                data-testid="client-login-btn"
              >
                Client Login
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                style={buttonStyle}
                className="btn-primary-hover"
                data-testid="get-started-btn"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div 
            className="md:hidden py-4 border-t border-slate-100"
            style={{ backgroundColor: 'var(--color-header-bg, #FFFFFF)' }}
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className="font-medium py-2"
                  style={navLinkStyle(isActive(link.to))}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-slate-100 flex flex-col gap-2">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full">Client Login</Button>
                </Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full" style={buttonStyle}>
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicHeader;
